import React, { useEffect } from 'react';
import { Link, useHistory } from "react-router-dom"
import CmsPageLayout from './layout/CmsPageLayout';
import { SEO_DEFAULT_DESCRIPTION, SEO_DEFAULT_KEYWORDS } from './config/constant';

export default function FaqSite() {
    const linkOuter = true;

    return (
        <CmsPageLayout
            title="MooveTrax – Frequently Asked Questions (FAQ)"
            lastUpdated={false}
            seoTitle="MooveTrax – Frequently Asked Questions (FAQ)"
            seoDescription={SEO_DEFAULT_DESCRIPTION}
            seoKeywords={SEO_DEFAULT_KEYWORDS}
            linkOuter={linkOuter}
        >
            <React.Fragment>
                <p className="page-subtitle no-margin"><b>General</b></p>
                <p className="page-subtitle no-margin"><b>What is MooveTrax?</b></p>
                <p>
                    MooveTrax is an all-in-one vehicle management platform designed for contactless car rentals, fleets, and vehicle owners. It combines GPS tracking, remote access, immobilization, reporting, maintenance tracking, alerts, and business tools into a single system.
                </p>

                <p className="page-subtitle"><b>Who is MooveTrax for?</b></p>
                <p>
                    MooveTrax is ideal for:
                </p>
                <ul>
                    <li>Contactless car rental operators</li>
                    <li>Peer-to-peer rental hosts</li>
                    <li>Small and large fleets</li>
                    <li>Shuttle and bus operators</li>
                    <li>Businesses managing staff vehicles</li>
                    <li>Vehicle owners who want advanced security and control</li>
                </ul>

                <p className="page-subtitle"><b>Contactless Car Rentals</b></p>
                <p className="page-subtitle no-margin"><b>Can MooveTrax support fully contactless car rentals?</b></p>
                <p>
                    Yes. MooveTrax is built specifically to enable a <b>100% contactless rental experience</b>.
                </p>

                <p className="page-subtitle"><b>How does a contactless rental work?</b></p>
                <ol>
                    <li>The renter books the vehicle</li>
                    <li>The owner sends the renter a secure access URL</li>
                    <li>The renter opens the link on their phone</li>
                    <li>The renter can:
                        <ul>
                            <li>See the vehicle’s <b>live location on a map</b></li>
                            <li><b>Honk the horn</b> to locate it</li>
                            <li><b>Flash the lights</b></li>
                            <li><b>Unlock the vehicle</b> when permitted</li>
                        </ul>
                    </li>
                </ol>
                <p>
                    No keys. No meetups. No handoffs.
                </p>

                <p className="page-subtitle"><b>Does the renter need to install an app?</b></p>
                <p>
                    No. Renters can access the vehicle using a <b>secure web link</b>.
                </p>

                <p className="page-subtitle"><b>Can renters be given temporary access?</b></p>
                <p>
                    Yes. Renters receive <b>time-limited access</b> that automatically expires at the end of the rental period.
                </p>
                <p>
                    Once access expires:
                </p>
                <ul>
                    <li>The vehicle locks</li>
                    <li>Starting is disabled</li>
                    <li>Control is revoked automatically</li>
                </ul>

                <p className="page-subtitle"><b>Can the owner require verification before unlocking or starting?</b></p>
                <p>
                    Yes. Owners can enforce <b>requirements before access is granted</b>, such as:
                </p>
                <ul>
                    <li>Uploading required photos (ID, vehicle condition, parking location, etc.)</li>
                    <li>Completing verification steps</li>
                </ul>
                <p>
                    Unlocking or starting will not work until requirements are met.
                </p>

                <p className="page-subtitle"><b>Vehicle Access & Control</b></p>
                <p className="page-subtitle no-margin"><b>Can owners remotely lock and unlock vehicles?</b></p>
                <p>
                    Yes. Owners can remotely:
                </p>
                <ul>
                    <li>Lock the vehicle</li>
                    <li>Unlock the vehicle</li>
                    <li>Enable (unkill) the engine</li>
                    <li>Disable (kill) the engine</li>
                </ul>
                <p>
                    All actions are logged.
                </p>

                <p className="page-subtitle"><b>Is there Backup access if there’s no cellular signal?</b></p>
                <p>
                    Yes. MooveTrax supports <b>Bluetooth proximity access</b> when the phone is paired.
                </p>
                <p>This allows unlocking and enabling the vehicle even if:</p>
                <ul>
                    <li>The vehicle has no cellular signal</li>
                    <li>The renter’s phone has no data signal</li>
                </ul>

                <p className="page-subtitle"><b>How does Bluetooth proximity access work?</b></p>
                <p>When paired:</p>
                <ul>
                    <li>Walking <b>up to the vehicle</b> automatically <b>unlocks and enables</b> it</li>
                    <li>Walking <b>away from the vehicle</b> automatically <b>locks and disables</b> it</li>
                </ul>
                <p>This provides a modern, hands-free experience similar to smart keys.</p>

                <p className="page-subtitle"><b>Tracking & History</b></p>
                <p className="page-subtitle no-margin"><b>Can I track vehicles in real time?</b></p>
                <p>Yes. MooveTrax provides <b>live GPS tracking</b> for all vehicles.</p>

                <p className="page-subtitle"><b>How much history does MooveTrax keep?</b></p>
                <p>MooveTrax stores up to <b>3 months of detailed history</b>, including:</p>
                <ul>
                    <li>Trip replay</li>
                    <li>Events and alerts</li>
                    <li>Commands and access logs</li>
                    <li>Mileage records</li>
                </ul>
                <p>(History length may vary by plan.)</p>

                <p className="page-subtitle"><b>Does MooveTrax log mileage?</b></p>
                <p>Yes. MooveTrax automatically logs:</p>
                <ul>
                    <li>Daily mileage totals</li>
                    <li>Mileage by vehicle</li>
                    <li>Mileage by date range</li>
                </ul>
                <p>Mileage reports can be exported.</p>

                <p className="page-subtitle"><b>Alerts & Notifications</b></p>
                <p className="page-subtitle no-margin"><b>Does MooveTrax send alerts?</b></p>
                <p>Yes. MooveTrax includes a <b>fully configurable notification system</b>.</p>
                <p>Alerts can be sent via:</p>
                <ul>
                    <li>Push notifications</li>
                    <li>Email</li>
                    <li>Multiple email recipients</li>
                </ul>

                <p className="page-subtitle"><b>What types of alerts are available?</b></p>
                <p>MooveTrax can notify you of:</p>
                <p><b>Access & Control</b></p>
                <ul>
                    <li>Lock / unlock</li>
                    <li>Engine enable / disable</li>
                    <li>Bluetooth unlocks</li>
                    <li>Horn and light commands</li>
                </ul>
                <p><b>Driving & Usage</b></p>
                <ul>
                    <li>Ignition on/off</li>
                    <li>Overspeeding</li>
                    <li>Over mileage limits</li>
                </ul>
                <p><b>Vehicle Health & Security</b></p>
                <ul>
                    <li>Low battery voltage (custom threshold)</li>
                    <li>Hood opened</li>
                    <li>Door opened</li>
                    <li>Shock or tampering</li>
                    <li>Possible towing</li>
                </ul>
                <p><b>Connectivity</b></p>
                <ul>
                    <li>Device offline</li>
                    <li>No position updates</li>
                    <li>No trip movement</li>
                    <li>Device suspension</li>
                </ul>
                <p><b>Environmental</b></p>
                <ul>
                    <li>Severe weather alerts</li>
                </ul>

                <p className="page-subtitle"><b>Audit Logs & Accountability</b></p>
                <p className="page-subtitle no-margin"><b>Does MooveTrax keep audit logs?</b></p>
                <p>Yes. MooveTrax maintains complete audit logs of all activity.</p>
                <p>Each record shows:</p>
                <ul>
                    <li>Who performed the action</li>
                    <li>What action was taken</li>
                    <li>Which vehicle was affected</li>
                    <li>When it happened</li>
                    <li>Whether it succeeded</li>
                </ul>

                <p className="page-subtitle"><b>Can I see who unlocked the vehicle?</b></p>
                <p>Yes. You can see <b>exactly which user or renter</b> unlocked or locked the vehicle and how (Bluetooth, web link, owner action, etc.).</p>
                <p>This is extremely useful for dispute resolution.</p>

                <p className="page-subtitle"><b>Battery Monitoring</b></p>
                <p className="page-subtitle no-margin"><b>Does MooveTrax monitor the vehicle’s battery?</b></p>
                <p>Yes. MooveTrax reads and records <b>vehicle battery voltage</b>.</p>
                <p>You can:</p>
                <ul>
                    <li>Monitor battery health</li>
                    <li>Receive low-battery alerts</li>
                    <li>Prevent dead batteries between rentals</li>
                </ul>
                <p>Battery voltage is logged alongside events.</p>

                <p className="page-subtitle"><b>Scheduling & Restrictions</b></p>
                <p className="page-subtitle no-margin"><b>Can I restrict when a vehicle can be driven?</b></p>
                <p>Yes. MooveTrax includes a <b>scheduler / timer</b> that can:</p>
                <ul>
                    <li>Prevent starting during certain hours</li>
                    <li>Automatically re-enable the vehicle later</li>
                </ul>

                <p className="page-subtitle"><b>What is this used for?</b></p>
                <p>Common uses include:</p>
                <ul>
                    <li>Rental enforcement</li>
                    <li>Staff vehicle restrictions</li>
                    <li>Parental controls</li>
                    <li>Preventing driving by parents or family members with dementia</li>
                </ul>

                <p className="page-subtitle"><b>Bus & Shuttle Tracking</b></p>
                <p className="page-subtitle no-margin"><b>Does MooveTrax support bus or shuttle tracking?</b></p>
                <p>Yes. MooveTrax supports <b>fleet tracking for buses and shuttles</b>.</p>

                <p className="page-subtitle"><b>Can passengers see where the bus is?</b></p>
                <p>Yes. Passengers can:</p>
                <ul>
                    <li>View live bus locations</li>
                    <li>See estimated arrival times</li>
                    <li>Track multiple vehicles</li>
                </ul>
                <p>This is ideal for schools, employee shuttles, hotels, and transit systems.</p>

                <p className="page-subtitle"><b>Toll Reporting</b></p>
                <p className="page-subtitle no-margin"><b>Does MooveTrax provide toll reports?</b></p>
                <p>Yes. MooveTrax generates <b>detailed toll reports</b> so rental owners can bill renters accurately.</p>
                <p>Reports include:</p>
                <ul>
                    <li>Date and time</li>
                    <li>Toll plaza location</li>
                    <li>Cost per toll</li>
                    <li>Total toll amount</li>
                </ul>

                <p className="page-subtitle"><b>Why is toll reporting important?</b></p>
                <p>Toll charges often arrive after a rental ends.</p>
                <p>MooveTrax ensures owners can <b>pass through toll costs transparently and accurately</b>.</p>

                <p className="page-subtitle"><b>Maintenance Tracking</b></p>
                <p className="page-subtitle no-margin"><b>Does MooveTrax track maintenance?</b></p>
                <p>Yes. MooveTrax provides <b>unlimited maintenance tracking</b> per vehicle.</p>

                <p className="page-subtitle"><b>How does maintenance tracking work?</b></p>
                <p>You can track maintenance based on:</p>
                <ul>
                    <li>Mileage</li>
                    <li>Days elapsed</li>
                    <li>Specific dates</li>
                    <li>Or any combination</li>
                </ul>

                <p className="page-subtitle"><b>What maintenance items can be tracked?</b></p>
                <p>Anything, including:</p>
                <ul>
                    <li>Oil changes</li>
                    <li>Tires</li>
                    <li>Brakes</li>
                    <li>Inspections</li>
                    <li>Battery replacement</li>
                    <li>Registration or compliance items</li>
                </ul>
                <p>There is no limit to how many items you track.</p>

                <p className="page-subtitle"><b>Are maintenance reminders sent?</b></p>
                <p>Yes. Maintenance reminders can be emailed to one or more recipients.</p>

                <p className="page-subtitle"><b>Profit & Loss (P&L) Tracking</b></p>
                <p className="page-subtitle no-margin"><b>Does MooveTrax include profit & loss tracking?</b></p>
                <p>Yes. MooveTrax includes a <b>built-in P&L tracker per vehicle</b>.</p>

                <p className="page-subtitle"><b>What can I track in P&L?</b></p>
                <p>You can track:</p>
                <p><b>Income</b></p>
                <ul>
                    <li>Rental income</li>
                    <li>Trip reference</li>
                    <li>Amount</li>
                    <li>Notes</li>
                    <li>Photo attachments</li>
                </ul>
                <p><b>Expenses</b></p>
                <ul>
                    <li>Fuel</li>
                    <li>Cleaning</li>
                    <li>Repairs</li>
                    <li>Maintenance</li>
                    <li>Miscellaneous costs</li>
                </ul>

                <p className="page-subtitle"><b>Can I attach photos to entries?</b></p>
                <p>Yes. You can attach <b>photos of receipts, invoices, or documentation</b> to income and expense entries.</p>

                <p className="page-subtitle"><b>Support & Subscription</b></p>
                <p className="page-subtitle no-margin"><b>Is a subscription required?</b></p>
                <p>Yes. A subscription covers:</p>
                <ul>
                    <li>Cellular connectivity</li>
                    <li>Platform access</li>
                    <li>Tracking and history</li>
                    <li>Alerts and reports</li>
                    <li>Contactless access features</li>
                </ul>

                <p className="page-subtitle"><b>What support options are available?</b></p>
                <ul>
                    <li><b>Phone Support:</b> Monday–Friday, <b>9:00 AM – 5:00 PM EST</b></li>
                    <li><b>Email Support:</b> 24/7 at <a href="mailto:support@moovetrax.com">support@moovetrax.com</a></li>
                </ul>

                <p className="page-subtitle"><b>Legal & Safety</b></p>
                <p className="page-subtitle no-margin"><b>Is MooveTrax legal?</b></p>
                <p>MooveTrax is legal when used on vehicles you own or are authorized to manage. Users are responsible for complying with local laws regarding tracking, immobilization, and disclosure.</p>

                <p className="page-subtitle"><b>Is immobilization safe?</b></p>
                <p>Immobilization should only be used when the vehicle is stationary and safe. MooveTrax provides controls and logs to ensure responsible use. Killswitch should be installed on your starter to prevent the starting of the car.</p>

            </React.Fragment>
        </CmsPageLayout>
    )
}
